import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { JobBrowserStoreProvider } from '@/providers/dashboard/JobBrowserContext';
import type { Density, ShellView } from '@/providers/dashboard/ShellContext';
import { ShellContext } from '@/providers/dashboard/ShellContext';
import { createJobBrowserStore } from '@/stores/jobBrowserStore';

interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardProvider = ({ children }: DashboardProviderProps) => {
  const [view, setView] = useState<ShellView>('feed');
  const [density, setDensity] = useState<Density>('comfortable');
  const [addUrlOpen, setAddUrlOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [pendingView, setPendingView] = useState<ShellView | null>(null);

  const requestView = useCallback(
    (next: ShellView) => {
      if (view === 'settings' && settingsDirty && next !== 'settings') {
        setPendingView(next);
      } else {
        setView(next);
      }
    },
    [view, settingsDirty],
  );

  const openAddUrl = useCallback(() => setAddUrlOpen(true), []);
  const closeAddUrl = useCallback(() => setAddUrlOpen(false), []);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  const resolvePendingView = useCallback(
    (confirmed: boolean) => {
      if (confirmed && pendingView) {
        setView(pendingView);
      }
      setPendingView(null);
    },
    [pendingView],
  );

  const shellValue = useMemo(
    () => ({
      addUrlOpen,
      closeAddUrl,
      closePalette,
      density,
      openAddUrl,
      openPalette,
      paletteOpen,
      pendingView,
      requestView,
      resolvePendingView,
      setDensity,
      setSettingsDirty,
      settingsDirty,
      setView,
      view,
    }),
    [
      addUrlOpen,
      closeAddUrl,
      closePalette,
      density,
      openAddUrl,
      openPalette,
      paletteOpen,
      pendingView,
      resolvePendingView,
      requestView,
      settingsDirty,
      view,
    ],
  );

  // --- JobBrowser (high-frequency) — now a context-scoped Zustand store ---
  const [jobBrowserStore] = useState(() => createJobBrowserStore());

  return (
    <ShellContext.Provider value={shellValue}>
      <JobBrowserStoreProvider value={jobBrowserStore}>
        {children}
      </JobBrowserStoreProvider>
    </ShellContext.Provider>
  );
};
